/**
 * Console Override for Production Builds
 * 
 * This module globally disables console output in production builds to prevent
 * exposing debug information to end users. It should be imported at the very
 * top of your application entry point (index.tsx).
 * 
 * Features:
 * - Automatically disables console in production when REACT_APP_ENABLE_PROD_CONSOLE=false
 * - Preserves original console methods for emergency debugging
 * - No-ops all console methods in production to prevent information leakage
 * - Works seamlessly with the devConsole utility
 * 
 * Import this at the top of index.tsx:
 *   import './utils/consoleOverride';
 */

const isProduction = process.env.NODE_ENV === 'production';
const enableProdConsole = process.env.REACT_APP_ENABLE_PROD_CONSOLE === 'true';

// Only override console in production when explicitly disabled
if (isProduction && !enableProdConsole) {
  // Save original console methods for emergency debugging
  const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
    trace: console.trace,
    table: console.table,
    group: console.group,
    groupEnd: console.groupEnd,
    groupCollapsed: console.groupCollapsed,
    dir: console.dir,
    dirxml: console.dirxml,
    assert: console.assert,
  };

  // Store original console for emergency debugging (accessible via browser console)
  (window as any).__originalConsole = originalConsole;

  // No-op function
  const noop = () => {};

  // Override all console methods with no-ops
  console.log = noop;
  console.info = noop;
  console.warn = noop;
  console.error = noop;
  console.debug = noop;
  console.trace = noop;
  console.table = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
  console.dir = noop;
  console.dirxml = noop;

  // Keep console.assert functional but silent
  console.assert = noop;

  // Log to original console that override is active (for debugging the build process)
  if (typeof window !== 'undefined') {
    originalConsole.log('%c[ShipmentCocaptain] Console output disabled in production build', 'color: #888; font-style: italic;');
    originalConsole.log('%c[ShipmentCocaptain] To enable console logs, set REACT_APP_ENABLE_PROD_CONSOLE=true in .env.production', 'color: #888; font-style: italic;');
    originalConsole.log('%c[ShipmentCocaptain] Access original console via window.__originalConsole', 'color: #888; font-style: italic;');
  }
}

export {};
